import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import './CustomerView.css';

// Use the production API URL
const API_URL = 'https://api.v1.s3dtechnology.in/api';
const RAZORPAY_KEY = 'rzp_test_TGzapDbUo3Jj9t';

function CustomerView() {
  const { paymentLinkId } = useParams();
  const navigate = useNavigate();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching order:', paymentLinkId);
      
      const response = await axios.get(`${API_URL}/order/${paymentLinkId}`);
      console.log('Order response:', response.data);
      
      if (response.data.success && response.data.order) {
        setOrder(response.data.order);
        
        if (response.data.order.status === 'PAID') {
          setPaymentStatus('success');
        }
      } else {
        setError('Order not found');
      }
    } catch (err) {
      console.error('Error:', err);
      if (err.response?.status === 404) {
        setError('This payment link is invalid or has expired.');
      } else {
        setError('Unable to load payment details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [paymentLinkId]);

  useEffect(() => {
    if (paymentLinkId) {
      fetchOrderDetails();
    } else {
      setError('Invalid payment link');
      setLoading(false);
    }
  }, [fetchOrderDetails, paymentLinkId]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!order) return;
    
    setProcessing(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please check your internet connection.');
        setProcessing(false);
        return;
      }

      const options = {
        key: RAZORPAY_KEY,
        amount: Math.round(order.amount * 100), // Amount in paise
        currency: 'INR',
        name: 'Payment System',
        description: `Payment for ${order.productName}`,
        image: 'https://cdn.razorpay.com/logos/rzp_logo.png',
        handler: async function (razorpayResponse) {
          console.log('Payment Success:', razorpayResponse);
          
          // Update order status in backend
          try {
            await axios.post(`${API_URL}/verify-payment`, {
              razorpayPaymentId: razorpayResponse.razorpay_payment_id,
              razorpayOrderId: razorpayResponse.razorpay_order_id,
              razorpaySignature: razorpayResponse.razorpay_signature,
              paymentLinkId: paymentLinkId
            });
          } catch (err) {
            console.log('Verification note:', err.message);
          }
          
          toast.success('✅ Payment successful!');
          setPaymentStatus('success');
        },
        prefill: {
          name: order.customerName,
          email: 'customer@example.com',
          contact: '9999999999'
        },
        notes: {
          orderId: order.id,
          paymentLinkId: paymentLinkId
        },
        theme: {
          color: '#667eea'
        },
        modal: {
          ondismiss: function () {
            setProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', function (response) {
        console.error('Payment Failed:', response.error);
        setPaymentStatus('failed');
        toast.error('Payment failed: ' + response.error.description);
        setProcessing(false);
      });

      razorpay.open();

    } catch (err) {
      console.error('Payment error:', err);
      setPaymentStatus('failed');
      toast.error('An error occurred. Please try again.');
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return '₹' + Number(amount || 0).toLocaleString('en-IN');
  };

  // Loading state
  if (loading) {
    return (
      <div className="customer-container">
        <div className="payment-card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
            <h3 style={{ marginTop: '20px', color: '#333' }}>Loading Payment Details</h3>
            <p style={{ color: '#666' }}>Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="customer-container">
        <div className="payment-card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>❌</div>
            <h2 style={{ color: '#ef4444', marginBottom: '12px' }}>Invalid Link</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>{error}</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (paymentStatus === 'success') {
    return (
      <div className="customer-container">
        <div className="payment-card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '5rem', marginBottom: '20px' }}>✅</div>
            <h2 style={{ color: '#22c55e', marginBottom: '8px' }}>Payment Successful!</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>
              Thank you for your payment, {order?.customerName}!
            </p>
            
            <div className="success-details">
              <div className="detail-row">
                <span>Amount Paid</span>
                <strong>{formatCurrency(order?.amount)}</strong>
              </div>
              <div className="detail-row">
                <span>Product</span>
                <strong>{order?.productName}</strong>
              </div>
              <div className="detail-row">
                <span>Status</span>
                <span className="status-badge paid">PAID</span>
              </div>
            </div>

            <button onClick={() => navigate('/')} className="btn-primary" style={{ marginTop: '24px' }}>
              Back to Store
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  if (paymentStatus === 'failed') {
    return (
      <div className="customer-container">
        <div className="payment-card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>❌</div>
            <h2 style={{ color: '#ef4444', marginBottom: '12px' }}>Payment Failed</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>Your payment could not be processed.</p>
            <button onClick={handlePayment} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Payment form
  return (
    <div className="customer-container">
      <div className="payment-card">
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🏪</div>
          <h2>Complete Your Payment</h2>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>Secure payment powered by Razorpay</p>
        </div>

        <div className="order-summary">
          <h3>📋 Order Summary</h3>
          <div className="detail-row">
            <span>Customer</span>
            <strong>{order?.customerName}</strong>
          </div>
          <div className="detail-row">
            <span>Product</span>
            <strong>{order?.productName}</strong>
          </div>
          <div className="detail-row total">
            <span>Total Amount</span>
            <strong style={{ fontSize: '1.3rem', color: '#667eea' }}>{formatCurrency(order?.amount)}</strong>
          </div>
        </div>

        <button
          onClick={handlePayment}
          disabled={processing}
          className="pay-button"
        >
          {processing ? 'Processing...' : `Pay ${formatCurrency(order?.amount)}`}
        </button>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <span style={{ color: '#999', fontSize: '0.8rem' }}>🔒 Secured by Razorpay</span>
        </div>
      </div>
    </div>
  );
}

export default CustomerView;